import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, CircularProgress, MenuItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { getUsers, saveUser, deleteUser } from '../../../store/actions/userActions';
import { RootState } from '../../../store/reducers';

const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'diretoria', label: 'Diretoria' },
    { value: 'coordenacao', label: 'Coordenação' },
    { value: 'secretaria', label: 'Secretaria' },
    { value: 'financeiro', label: 'Financeiro' },
    { value: 'professor', label: 'Professor' },
    { value: 'aluno', label: 'Aluno' },
    { value: 'conselho', label: 'Conselho' },
    { value: 'sem acesso', label: 'Sem Acesso' },
];

const Users = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const users = useSelector((state: RootState) => state.users.users);
    const isFetching = useSelector((state: RootState) => state.users.isFetching);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState<any>(null);
    const [formData, setFormData] = useState<any>({ username: '', email: '', password: '', role: '' });
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    useEffect(() => {
        dispatch(getUsers());
    }, [dispatch]);

    const handleOpen = (item?: any) => {
        if (item) {
            setEditItem(item);
            setFormData({ username: item.username || '', email: item.email || '', password: '', role: item.role || '' });
        } else {
            setEditItem(null);
            setFormData({ username: '', email: '', password: '', role: '' });
        }
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditItem(null);
        setFormData({ username: '', email: '', password: '', role: '' });
    };

    const handleSave = () => {
        const payload: any = { username: formData.username, email: formData.email, role: formData.role };
        if (editItem) {
            payload.id = editItem.id;
            if (formData.password) {
                payload.password = formData.password;
            }
        } else {
            payload.password = formData.password;
        }
        dispatch(saveUser(payload));
        handleClose();
        enqueueSnackbar('Salvo com sucesso!', { variant: 'success' });
    };

    const handleDelete = (id: number) => {
        dispatch(deleteUser(id));
        setDeleteConfirm(null);
        enqueueSnackbar('Excluido com sucesso!', { variant: 'success' });
    };

    const handleChange = (field: string, value: string) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                display: false,
                filter: false,
                sort: true,
            }
        },
        {
            name: 'username',
            label: 'Usuário',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'email',
            label: 'Email',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'role',
            label: 'Perfil',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'actions',
            label: 'Ações',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = users[dataIndex];
                    return (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(item.id);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'checkbox' as const,
        selectableRows: 'none' as const,
        responsive: 'standard' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        textLabels: {
            body: { noMatch: 'Nenhum registro encontrado', toolTip: 'Ordenar' },
            pagination: { next: 'Próxima', previous: 'Anterior', rowsPerPage: 'Linhas por página:', displayRows: 'de' },
            toolbar: { search: 'Buscar', downloadCsv: 'Download CSV', print: 'Imprimir', viewColumns: 'Colunas', filterTable: 'Filtrar' },
            filter: { all: 'Todos', title: 'Filtros', reset: 'Limpar' },
        },
        onRowClick: (_rowData: any, rowMeta: any) => {
            const item = users[rowMeta.dataIndex];
            handleOpen(item);
        },
    };

    if (isFetching) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Usuários</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                    Novo Usuário
                </Button>
            </Box>

            <Paper>
                <MUIDataTable
                    title=""
                    data={users || []}
                    columns={columns}
                    options={options}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editItem ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Usuário"
                            value={formData.username}
                            onChange={(e) => handleChange('username', e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label={editItem ? "Nova Senha (deixe vazio para manter)" : "Senha"}
                            type="password"
                            value={formData.password}
                            onChange={(e) => handleChange('password', e.target.value)}
                            fullWidth
                            required={!editItem}
                        />
                        <TextField
                            label="Perfil"
                            select
                            value={formData.role}
                            onChange={(e) => handleChange('role', e.target.value)}
                            fullWidth
                            required
                        >
                            {roles.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!formData.username || !formData.email || (!editItem && !formData.password)}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este usuário?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    <Button
                        onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)}
                        color="error"
                        variant="contained"
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Users;
